import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { AppTableComponent, SelectedEvent } from '../../shared/table/table.component';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { I18nMap } from '../../../../models/entities/i18n-map';
import { TableTypeNames } from '../../../../services/column.service';
import { ColumnMap } from '../../../../models/entities/column-map';
import { PropertyManagementCompanyService } from '../../../../services/property-management-company/property-management-company.service';
import { PropertyManagementCompanyDataService } from '../../../../services/property-management-company/property-manager-company-data.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ScrollService } from '../../../../services/scroll.service';
import { GrowlerService } from '../../../../services/growler.service';
import { switchMap } from 'rxjs/operators';
import { AvatarBlockModule } from '../../shared/avatar-block/avatar-block.module';
import { I18nPluralPipe, NgIf } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { EmptyCellDirective } from '../../shared/table/table.directive';
import { LegfiPhonePipeModule } from '../../../../pipes/format/legfi-phone.module';
import { EmptyStateModule } from '../../shared/empty-state/empty-state.module';
import { User } from '../../../../models/entities/user';
import { Membership } from '../../../../models/entities/membership';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterButtonValue } from '../../../../models/entities/router-button-value';

@UntilDestroy()
@Component({
    selector: 'app-team-list',
    templateUrl: './team-list.component.html',
    standalone: true,
    imports: [
        PageHeaderComponent,
        AppTableComponent,
        AvatarBlockModule,
        I18nPluralPipe,
        MatSortModule,
        MatTableModule,
        EmptyCellDirective,
        RouterLink,
        LegfiPhonePipeModule,
        EmptyStateModule,
        NgIf,
        MatButtonModule,
        MatIconModule,
    ],
})
export class TeamListComponent implements OnInit, AfterViewInit
{
    @ViewChild(AppTableComponent) tableRef: AppTableComponent<User>;
    @ViewChild(MatSort, { static: true }) columnSort: MatSort;

    routerAddButton: RouterButtonValue;
    hasData = false;

    readonly organizationsMapping: I18nMap = {
        '=0': 'No organizations',
        '=1': '1 organization',
        'other': '# organizations',
    };
    readonly tableType = TableTypeNames.PMC_TEAM;
    readonly columnMap: ColumnMap[] = [
        {key: 'mobile', title: 'User', exportAs: 'user', isMobile: true},
        {key: 'name', title: 'User', exportAs: 'user'},
        {key: 'role', title: 'Role', exportAs: 'role'},
        {key: 'email', title: 'Email', exportAs: 'email'},
        {key: 'phone', title: 'Phone', exportAs: 'phone'},
    ];

    constructor(
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _activatedRoute: ActivatedRoute,
            private _router: Router,
            private _scrollService: ScrollService,
            private _growler: GrowlerService,
    ) {
    }

    get pmcId()  {
        return this._propertyManagementCompanyData.pmcId;
    }

    ngOnInit() {
        this._scrollService.scrollToTop();

        this.routerAddButton = {
            label: 'Team Member',
            route: `/pmc/${this.pmcId}/team/add`,
            ariaLabel: 'Add new member',
        };
    }

    ngAfterViewInit() {
        this.setupTable();
    }

    getOrganizationsCount(memberships: Membership[]) {
        return memberships?.filter((membership) => !!membership.organization).length || 0;
    }

    goToTeamMember(event: SelectedEvent<User>) {
        return this._router.navigate([event.source.id, 'details'], { relativeTo: this._activatedRoute });
    }

    readonly sortingDataAccessor = (data: User, sortHeaderId: string) => {
        if (sortHeaderId === 'phone') {
            return data.memberships[0].profile.phone;
        }

        if (sortHeaderId === 'role') {
            return !!data.propertyManagementCompanyUser;
        }

        return this.tableRef.defaultSorting(data, sortHeaderId);
    };

    private setupTable() {
        this.tableRef.connectTable$.pipe(
                switchMap(() => {
                    if (this.pmcId) {
                        return this._propertyManagementCompanyService.getPropertyManagementTeamMembers(this.pmcId);
                    }
                    return;
                }),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => this.tableRef.data$.next(res),
            error: () => {
                this.tableRef.loading = false;
                this._growler.oops('Unable to load team members');
            },
        });
    }
}
